import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzWaveModule } from 'ng-zorro-antd/core/wave';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { LuxonUtils } from '../../shared/utils/luxon-utils';

@Component({
  selector: 'app-date-range-selector',
  templateUrl: './date-range-selector.component.html',
  styleUrls: ['./date-range-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NzDatePickerModule, FormsModule, NgIf, NzButtonModule, NzWaveModule, NzIconModule],
})
export class DateRangeSelectorComponent {
  dateList: Date[] = [];

  // BUG: ShowTime cannot trigger the ngModelChange
  @Input() showTime = false;

  @Input() showAllTimeButton: boolean = true;

  @Input() startDate?: DateTime;
  @Input() endDate?: DateTime;

  @Input() disabledBefore?: DateTime;
  @Input() disabledAfter?: DateTime;

  @Output() rangeChange = new EventEmitter<{ startDate?: DateTime; endDate?: DateTime }>();
  @Output() startOrEndChange = new EventEmitter<{ startDate?: DateTime; endDate?: DateTime }>();

  ngOnInit() {
    this.startEndInputChange();
  }

  disabledDate = (current: Date): boolean => {
    if (!current) {
      return false;
    }

    if (!this.disabledBefore && !this.disabledAfter) {
      return false;
    }

    const currentDateTime = LuxonUtils.toDateTimeKeepLocalTime(current)!;

    if (this.disabledBefore) {
      if (currentDateTime < this.disabledBefore) {
        return true;
      } else {
        return false;
      }
    }

    if (this.disabledAfter) {
      if (currentDateTime > this.disabledAfter) {
        return true;
      } else {
        return false;
      }
    }

    return false;
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['startDate']?.previousValue !== changes['startDate']?.currentValue) {
      if (
        this.dateList.length === 2 &&
        this.startDate &&
        LuxonUtils.toJSDateKeepLocalTime(this.startDate)!.toISOString() !== this.dateList[0].toISOString()
      ) {
        this.startEndInputChange();
      } else if (!this.startDate && this.dateList.length === 2) {
        this.startEndInputChange();
      } else if (this.startDate && this.dateList.length !== 2) {
        this.startEndInputChange();
      }
    }

    if (changes['endDate']?.previousValue !== changes['endDate']?.currentValue) {
      if (
        this.dateList.length === 2 &&
        this.endDate &&
        LuxonUtils.toJSDateKeepLocalTime(this.endDate)!.toISOString() !== this.dateList[1].toISOString()
      ) {
        this.startEndInputChange();
      } else if (!this.endDate && this.dateList.length === 2) {
        this.startEndInputChange();
      } else if (this.endDate && this.dateList.length !== 2) {
        this.startEndInputChange();
      }
    }
  }

  startEndInputChange() {
    if (this.startDate && this.endDate) {
      this.dateList = [
        LuxonUtils.toJSDateKeepLocalTime(this.startDate)!,
        LuxonUtils.toJSDateKeepLocalTime(this.endDate)!,
      ];
    } else {
      this.dateList = [];
    }
  }

  onStartEndChange(dateList: Date[]) {
    const dateTimeList = dateList.map((c) => LuxonUtils.toDateTimeKeepLocalTime(c));

    if (dateTimeList.length !== 2) {
      this.rangeChange.emit({ startDate: undefined, endDate: undefined });
      return;
    }

    this.dateList = dateList;

    this.rangeChange.emit({
      startDate: dateTimeList[0],
      endDate: dateTimeList[1],
    });
  }

  onCalendarChange(dateList: (Date | null)[]) {
    console.log('------- dateList:', dateList);

    const dateTimeList = dateList.map((c) => LuxonUtils.toDateTimeKeepLocalTime(c));

    if (dateTimeList.length === 0) {
      this.startOrEndChange.emit({ startDate: undefined, endDate: undefined });
      return;
    } else if (dateTimeList.length === 1) {
      this.startOrEndChange.emit({ startDate: dateTimeList[0], endDate: undefined });
      return;
    }

    this.startOrEndChange.emit({
      startDate: dateTimeList[0],
      endDate: dateTimeList[1],
    });
  }

  get isAllTimeType() {
    if (this.dateList?.length > 0) {
      return 'dashed';
    } else {
      return 'primary';
    }
  }

  onAllTimeClick() {
    this.dateList = [];
    this.rangeChange.emit({ startDate: undefined, endDate: undefined });
  }
}
